import React, { FC, useEffect, useRef, useState } from 'react';
import { EditCloseProjectModalProps } from './EditCloseProjectModalProps';
import useTranslation from '../../hooks/useTranslation';
import CustomModal from '../../components/CustomModal';
import ProjectForm from '../../components/ProjectForm';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import CloseProjectTabs from '../../components/CloseProjectTabs';
import { useTypedSelector } from '../../store';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { getBusinessUnits } from '../../store/selectData/selectors';
import { selectConfidentialPermission } from '../../store/userData/selectors';
import {
  fetchSiteLocation,
  updateProject,
} from '../../store/projects/actionWrapper';
import { useDispatch } from 'react-redux';
import { returnCloseValidationSchema } from '../CloseProjectModal/utils';
import { selectSiteLocation } from '../../store/projects/selectors';
import { calculateTabErrors } from '../CreateNewUnitModal/utils';
import { modalsActions } from '../../store/modals/slice';
import { ProjectFormRef } from '../../components/ProjectForm/ProjectForm';

const EditCloseProjectModal: FC<EditCloseProjectModalProps> = ({
  projectsList,
  project,
  closeHandler,
  establishmentType,
  countries,
  setActiveProject,
  disableRestrictions,
  additionalAction,
  readonly,
  submitTrigger,
  directiveOpen: initialDirectiveOpen,
}) => {
  const dispatch = useDispatch();
  const editCloseProjectHandler = (data) => {
    submitTrigger();
    dispatch(
      updateProject({
        project: {
          ...data,
          establishmentType: ESTABLISHMENT_TYPE.CLOSE,
        },
        action: additionalAction,
      }),
    );
    setFormModified(false);
    closeHandler();
  };
  const [statusUpdateConfirmationModal, setStatusUpdateConfiramtionModal] =
    useState(false);
  const [directiveOpen, setDirectiveOpen] = useState(initialDirectiveOpen);
  useEffect(() => {
    setDirectiveOpen(initialDirectiveOpen);
  }, [initialDirectiveOpen, establishmentType]);
  const formRef = useRef<ProjectFormRef>(null);

  const isConfidentialAllowed = useTypedSelector(selectConfidentialPermission);
  const t = useTranslation();
  const BuList = useTypedSelector(getBusinessUnits);
  const usersList = useTypedSelector(selectResponsibleOptionsList);
  const selectedSiteLocation = useTypedSelector(selectSiteLocation);
  const [formModified, setFormModified] = React.useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = React.useState(
    project?.countryCode || '',
  );
  const validation = returnCloseValidationSchema(t);
  if (!project) return <></>;
  const onCountryChange = (country) => {
    setSelectedCountry(country.target.value);
  };

  const onBUChange = (bu) => {
    dispatch(fetchSiteLocation(bu.businessUnitId || ''));
  };

  const BuFiltered = BuList.filter(
    (bu) => bu.countryId === (selectedCountry || project?.countryCode),
  );
  const openStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(true);
  };
  const closeStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(false);
  };

  const updateStatusHandler = (id) => {
    setActiveProject(id);
    dispatch(modalsActions.openProjectStatusModal());
  };

  const updateStatusClicked = (id: string) => {
    if (formModified) {
      openStatusConfirmationModal();
      return;
    }
    updateStatusHandler(id);
  };

  const proceedWithStatusChange = () => {
    formRef.current.submit();
    closeStatusConfirmationModal();
    updateStatusHandler(project.id);
  };

  return (
    <CustomModal
      isVisible={establishmentType === ESTABLISHMENT_TYPE.CLOSE}
      useConfirmation
      formModified={formModified}
      onClose={closeHandler}
      title={
        directiveOpen ? t('labels.projectDirective') : t('common.editProject')
      }
      onBackBtnClick={directiveOpen ? () => setDirectiveOpen(false) : undefined}
      statusConfirmationModal={statusUpdateConfirmationModal}
      closeStatusConfirmationModal={closeStatusConfirmationModal}
      proceedStatusUpdate={proceedWithStatusChange}
      children={
        <ProjectForm
          onSubmit={editCloseProjectHandler}
          ref={formRef}
          directiveOpen={directiveOpen}
          onDirectiveBack={() => setDirectiveOpen(false)}
          setFormModified={(isDirty) => setFormModified(isDirty)}
          project={project}
          validationSchema={validation}
          onClose={closeHandler}
          isEdit
          selectedSiteLocation={selectedSiteLocation}
          submitBtnTitle={t('common.update')}
          projectType={project?.establishmentType}
          setFormErrors={setFormErrors}
          disableRestrictions={disableRestrictions}
          readOnly={readonly}
        >
          <CloseProjectTabs
            projectsList={projectsList}
            currentProject={project}
            countries={countries}
            setActiveProject={updateStatusClicked}
            usersList={usersList}
            BuList={BuFiltered}
            onCountryChange={onCountryChange}
            onBUChange={onBUChange}
            tabErrors={calculateTabErrors(formErrors, true)}
            isConfidentialAllowed={isConfidentialAllowed}
            isEdit
            directiveOpen={directiveOpen}
            onShowDirective={() => setDirectiveOpen(true)}
            onHideDirective={() => setDirectiveOpen(false)}
          />
        </ProjectForm>
      }
    />
  );
};

export default EditCloseProjectModal;
